import { Outlet, useNavigate } from 'react-router-dom';

import { ROUTES } from '@/constants/routes';
import { useProject } from '@/context/ProjectContext';
import { useAuth } from '@/features/auth/AuthContext';

import { ProjectSwitcher } from './ProjectSwitcher';
import { SidebarNav } from './SidebarNav';
import { STAGE_LABELS, type WorkflowStage } from './nav-items';
import { UserMenu } from './UserMenu';

export default function Layout() {
  const { selected, projects, setSelected, loading } = useProject();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const isAdmin = user?.role === 'admin';
  const isPM = user?.role === 'pm' || isAdmin;
  const isQA = user?.role === 'qa';
  const canSeePmOnly = isPM || isQA;
  const stage: WorkflowStage = (selected?.workflow_stage as WorkflowStage) || 'collection';

  // Block surveyors — dashboard is for Admin/PM/QA only.
  if (user && !canSeePmOnly) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <h2 className="mb-2 text-xl font-semibold text-gray-900">Access Restricted</h2>
          <p className="mb-4 text-gray-500">
            The dashboard is for Project Managers and QA reviewers.
          </p>
          <p className="text-sm text-gray-400">Surveyors use the mobile app for data collection.</p>
          <button
            onClick={() => {
              logout();
              navigate(ROUTES.LOGIN);
            }}
            className="mt-4 rounded-lg bg-indigo-600 px-4 py-2 text-sm text-white transition-colors hover:bg-indigo-500"
          >
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50 text-gray-900">
      <aside className="flex w-60 flex-shrink-0 flex-col border-r border-gray-200 bg-white">
        <div className="border-b border-gray-200 px-5 py-5">
          <img
            src="/cpcon-logo.png"
            alt="CPCON"
            className="mb-1 h-auto w-[140px]"
            width={140}
          />
          <p className="text-xs text-gray-400">CMMS Survey System</p>

          {!loading && (
            <ProjectSwitcher projects={projects} selected={selected} onChange={setSelected} />
          )}

          {selected && <p className="mt-1 truncate text-xs text-gray-400">{selected.client_name}</p>}
        </div>

        {selected && (
          <div className="border-b border-gray-200 px-5 py-2">
            <p className="mb-1 text-[10px] uppercase tracking-wider text-gray-400">
              Workflow Stage
            </p>
            <span className="inline-flex items-center rounded border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-600">
              {STAGE_LABELS[stage] ?? stage}
            </span>
          </div>
        )}

        <SidebarNav
          currentStage={stage}
          canSeePmOnly={canSeePmOnly}
          hasComponentization={Boolean(selected?.has_componentization)}
        />

        <UserMenu />
      </aside>

      <main className="flex-1 overflow-auto">
        <div className="mx-auto max-w-7xl px-6 py-6">
          {loading ? (
            <div className="flex h-64 items-center justify-center">
              <div
                role="status"
                aria-label="Loading"
                className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent"
              />
            </div>
          ) : !selected ? (
            <div className="py-20 text-center text-gray-400">
              <p className="text-lg">No projects found</p>
              <p className="mt-1 text-sm">Create a project in the backend first.</p>
            </div>
          ) : (
            <Outlet />
          )}
        </div>
      </main>
    </div>
  );
}
